import {
  Component,
  Input,
  SimpleChanges,
  ViewChild,
  Output,
  TemplateRef,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { CompsTable, CompsTableRow } from "src/app/comps-page/comps-page.model";
import { MatTableDataSource, MatTable } from "@angular/material/table";
import { faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { MatSnackBarRef } from "@angular/material/snack-bar";
import { AppState } from "src/app/store/state/app.state";
import { Store } from "@ngrx/store";
import {
  SetDpParams,
  SetAuditCallFbar,
  DeleteDpParams,
} from "src/app/store/actions/global-subheader.actions";
import { ActivatedRoute } from "@angular/router";
import { VAIconRegistryService, close } from "@va/core-library/icons";
import { MatPaginator } from "@angular/material/paginator";
import { Observable } from "rxjs";

@Component({
  selector: "app-comparative-grid",
  templateUrl: "./comparative-grid.component.html",
  styleUrls: ["./comparative-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComparativeGridComponent {
  @Input() isVisible = true;
  @Input() metaReady: boolean;
  @Input() dataReady: boolean;
  @Input() public dataSource$: Observable<any>;
  compsTableData: CompsTable = {
    rows: [],
    columns: [],
    marketColumns: [],
    columnData: {},
    filter: {},
    rowData: {},
    extras: {},
    title: "",
  };
  @Input() isTimeSeries: boolean;
  @Input() hasFuzzyPanel: boolean;
  @Input() hasUnsavedData: boolean;
  @Input() fuzzySwitch: boolean;
  @Input() hasContextMenu: boolean;
  @Input() hasFormulaBar: boolean;
  @Input() maxHeight: number;
  @Input() tableClassName: string;
  @Input() allowTickerDeletion = true;
  @Input() showCompanyCount = false;
  @Input() sortByColumnId = "";
  @Input() selectedLineCell = "";
  @Input() resetSortonDataReady: boolean = false;
  @Output() onCellClick: EventEmitter<any> = new EventEmitter();
  @Output() onDeleteTicker: EventEmitter<any> = new EventEmitter();

  @ViewChild("successTemplate") successTemplate: TemplateRef<any>;
  contextMenuPosition = { x: "0px", y: "0px" };
  compsTableDataRowsDefaultOrer: Array<CompsTableRow>;
  staticDisplayColumns = ["ticker", "company", "yearend"];
  displayColumns = [];
  tableWidth = 370;
  selectedTicker = "";
  sortDirection = "";
  activeSort = "";
  snackbarMessage = "";
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  selectedPeriod: string;
  showValue: boolean;
  showYoy: boolean;
  showRevision: boolean;
  showMedian: boolean;
  showAverage: boolean;
  showTotal: boolean;
  lineHasTotal: boolean;
  revision: string;
  source: string;
  currency: string;
  // tickerDelete = false;
  lineHasPeersMapping = {};
  contextMenuReady = false;
  snackBarRef: MatSnackBarRef<any>;
  currentAbsCalendarYear = `CY-${new Date().getFullYear()}`;
  currentAbsFiscalYear = `FY-${new Date().getFullYear()}`;
  marketColumns = [];

  originalCompsTableRows = [];

  public matDataSource = new MatTableDataSource<any>();
  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  constructor(
    private ref: ChangeDetectorRef,
    private store: Store<AppState>,
    protected route: ActivatedRoute,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([close]);
  }

  ngAfterViewInit() {
    this.matDataSource.paginator = this.paginator;
    this.dataSource$.subscribe((data) => {
      this.compsTableData = data;
      this.matDataSource.data = data.rows;
      if (data.rows) {
        this.originalCompsTableRows = JSON.parse(JSON.stringify(data.rows));
      }
      const dataColumnKeys = data.columns;
      this.marketColumns = data.marketColumns || [];
      this.selectedPeriod = data.filter.selectedPeriod;
      this.showValue = data.filter.showValue;
      this.showYoy = data.filter.showYoy;
      this.showRevision = data.filter.showRevision;
      this.showMedian = data.filter.showMedian;
      this.showAverage = data.filter.showAverage;
      this.showTotal = data.filter.showTotal;
      this.lineHasTotal = data.filter.lineHasTotal;
      this.source = data.filter.source;
      this.currency = data.filter.currency;
      this.revision = data.filter.revision;
      this.displayColumns = [...this.staticDisplayColumns];

      const marketColumnsLength = this.marketColumns.length;
      if (this.showValue && this.showYoy) {
        this.displayColumns.push("datatype");
        this.tableWidth =
          420 + (dataColumnKeys.length + marketColumnsLength) * 136 + 100;
      } else {
        this.tableWidth =
          370 + (dataColumnKeys.length + marketColumnsLength) * 136 + 100;
      }
      this.marketColumns.forEach((item) => {
        this.displayColumns.push(`${item.id}`);
      });
      dataColumnKeys.forEach((colKey) => {
        this.displayColumns.push(`${colKey}`);
      });
      if (!this.sortDirection) {
        this.sortDirection = "desc";
      }

      this.sortData(this.sortByColumnId, this.sortDirection);

      // The important part:
      this.ref.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.isVisible) {
      this.selectedLineCell = "";
      this.selectedTicker = "";
    }
    if (changes.dataReady && changes.dataReady.currentValue) {
      this.selectedLineCell = "";
      this.selectedTicker = "";
      this.store.dispatch(new DeleteDpParams());
    }
  }

  onRowClick(rowObj: CompsTableRow) {
    this.selectedTicker = rowObj.ticker;
    this.selectedLineCell = "";
  }

  onLineCellClick(ticker, colKey, valueType: string, event) {
    const sliCompsData = this.isTimeSeries
      ? this.compsTableData.rowData[ticker][
          this.compsTableData.columnData[colKey].sliCompsId
        ]
      : this.compsTableData.rowData[ticker][colKey];
    this.selectedTicker = ticker;
    const period = this.isTimeSeries ? colKey : this.selectedPeriod;
    if (
      sliCompsData &&
      sliCompsData[period] &&
      sliCompsData[period][valueType] !== undefined &&
      sliCompsData[period][valueType] !== null &&
      !(
        (valueType === "yoy" || valueType === "revision") &&
        sliCompsData[period]["has_no_fb"]
      )
    ) {
      //process dpParam call only if its a new data cell selection
      if (
        this.hasFormulaBar &&
        this.selectedLineCell !== `${ticker}_${valueType}_${colKey}`
      ) {
        this.onCellClick.emit();
        const today = new Date();
        const mm = today.getMonth() + 1;
        const dd = today.getDate();
        const yyyy = today.getFullYear();

        const sliCompsId = this.isTimeSeries
          ? this.compsTableData.columnData[colKey].sliCompsId
          : colKey;
        const dpParams = {
          dp: `${
            this.compsTableData.rowData[ticker][`${sliCompsId}|RD`][period][
              valueType
            ]
          }|${mm}/${dd}/${yyyy}`, //"16151916582|09/02/2020",
          cut: "STD",
          ticker: ticker,
          // source: this.source.toLowerCase(),
          growthType: valueType === "values" ? "Analytics" : "YoY%",
          shouldLog: true,
          revision: "",
          period: period,
          extras: `${
            this.compsTableData.rowData[ticker][`${sliCompsId}|RD`][period][
              valueType
            ]
          }|${period}`, //"16151916582|CY-2020"
        };
        if (this.source === "VA Actuals/Consensus") {
          if (
            this.compsTableData.rowData[ticker][sliCompsId][period]
              .is_forecast === false
          ) {
            dpParams["source"] = "va actuals";
          } else {
            dpParams["source"] = "consensus";
          }
        } else {
          dpParams["source"] = `${encodeURIComponent(
            this.source.toLowerCase(),
          )}`;
        }

        if (dpParams.growthType === "YoY%") {
          dpParams["historical_focus"] = "VA Actuals";
        }
        if (this.compsTableData.columnData[colKey].isFuzzy) {
          dpParams["enable_fuzzy"] = true;
        }
        if (this.currency !== "Base") {
          dpParams["currency"] = this.currency;
        }
        this.store.dispatch(new SetDpParams(dpParams));
      }
      this.selectedLineCell = `${ticker}_${valueType}_${colKey}`;
    } else {
      //there is no data
      this.selectedLineCell = "";
      this.store.dispatch(new DeleteDpParams());
    }
    if (event) {
      event.stopPropagation(); // prevent onRowClick
    }
  }

  onMarketLineCellClick(ticker, colKey, valueType: string, event) {
    const data = this.compsTableData.rowData[ticker].market_data[colKey];
    const valueExist = data && typeof data[valueType] === "number";

    this.selectedTicker = ticker;

    if (valueExist) {
      //process dpParam call only if its a new data cell selection
      if (
        this.hasFormulaBar &&
        this.selectedLineCell !== `${ticker}_${valueType}_${colKey}`
      ) {
        this.onCellClick.emit();
        const today = new Date();
        const mm = today.getMonth() + 1;
        const dd = today.getDate();
        const yyyy = today.getFullYear();
        const period = `CY-${yyyy}`;

        const dpParams = {
          dp: `${data.rd[valueType]}|${mm}/${dd}/${yyyy}`, //"16151916582|09/02/2020",
          cut: "STD",
          ticker: ticker,
          // source: this.source.toLowerCase(),
          growthType: valueType === "values" ? "Analytics" : "YoY%",
          shouldLog: true,
          revision: "",
          period,
          extras: `${data.rd[valueType]}|${period}`, //"16151916582|CY-2020"
          isUniversalsliid: true,
        };
        if (this.source === "VA Actuals/Consensus") {
          dpParams["source"] = "consensus";
        } else {
          dpParams["source"] = `${encodeURIComponent(
            this.source.toLowerCase(),
          )}`;
        }

        if (dpParams.growthType === "YoY%") {
          dpParams["historical_focus"] = "VA Actuals";
        }
        if (this.compsTableData.columnData[colKey].isFuzzy) {
          dpParams["enable_fuzzy"] = true;
        }
        if (this.currency !== "Base") {
          dpParams["currency"] = this.currency;
        }
        this.store.dispatch(new SetDpParams(dpParams));
      }
      this.selectedLineCell = `${ticker}_${valueType}_${colKey}`;
    } else {
      //there is no data
      this.selectedLineCell = "";
      this.store.dispatch(new DeleteDpParams());
    }
    if (event) {
      event.stopPropagation(); // prevent onRowClick
    }
  }

  formatCell(data, currency, colSliCompId, valueType: string) {
    const sliCompsData = data;
    if (sliCompsData === undefined || sliCompsData === null) {
      return "-";
    }

    const valueToUse = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(Math.abs(sliCompsData));
    let unitType = this.compsTableData.columnData[colSliCompId].unit;
    if (!currency || valueType === "yoy" || valueType === "revision") {
      currency = "";
    }
    if (unitType === "%") {
      unitType = valueType === "yoy" || valueType === "revision" ? "bps" : "%";
    } else {
      unitType = valueType === "yoy" || valueType === "revision" ? "%" : "";
    }
    if (sliCompsData < 0) {
      return `(${currency}${valueToUse}${unitType})`;
    }
    return `${currency}${valueToUse}${unitType}`;
  }

  sortData(colKey, sortDirection = "") {
    if (sortDirection) {
      this.sortDirection = sortDirection;
    } else {
      if (colKey === this.activeSort) {
        this.sortDirection =
          this.sortDirection === ""
            ? "asc"
            : this.sortDirection === "asc"
              ? "desc"
              : "";
      } else {
        this.sortDirection = "asc";
      }
    }
    this.activeSort = colKey;
    switch (colKey) {
      case "company":
        if (this.sortDirection === "") {
          this.matDataSource.data = [...this.originalCompsTableRows];
          this.activeSort = "";
        } else {
          this.matDataSource.data = this.matDataSource.data.sort((a, b) => {
            if (this.sortDirection === "asc") {
              return a.full_name.localeCompare(b.full_name);
            }

            return b.full_name.localeCompare(a.full_name);
          });
        }
        this.table.renderRows();
        break;
      case "period":
        if (this.sortDirection === "") {
          this.matDataSource.data = [...this.originalCompsTableRows];
          this.activeSort = "";
        } else {
          this.matDataSource.data = this.matDataSource.data.sort((a, b) => {
            const firstValue =
              (this.compsTableData.rowData[a.ticker] &&
                this.compsTableData.rowData[a.ticker].relative_period_Y1) ||
              "";
            const secondValue =
              (this.compsTableData.rowData[b.ticker] &&
                this.compsTableData.rowData[b.ticker].relative_period_Y1) ||
              "";
            if (this.sortDirection === "asc") {
              return firstValue.localeCompare(secondValue);
            }

            return secondValue.localeCompare(firstValue);
          });
        }
        this.table.renderRows();
        break;
      default:
        //sort for data columns
        if (this.sortDirection === "") {
          this.matDataSource.data = [...this.originalCompsTableRows];
          this.activeSort = "";
        } else {
          const sortKey =
            this.showValue && this.showYoy
              ? "values"
              : this.showValue
                ? "values"
                : this.showYoy
                  ? "yoy"
                  : this.showRevision
                    ? "revision"
                    : "values";
          const isMarketKey = this.checkIfMarketKey(colKey);
          const period = this.isTimeSeries ? colKey : this.selectedPeriod;
          this.matDataSource.data = this.matDataSource.data.sort((a, b) => {
            let aSliData = null;
            let bSliData = null;
            if (isMarketKey) {
              aSliData =
                this.compsTableData.rowData[a.ticker] &&
                this.compsTableData.rowData[a.ticker].market_data &&
                this.compsTableData.rowData[a.ticker].market_data[colKey] &&
                this.compsTableData.rowData[a.ticker].market_data[colKey][
                  sortKey
                ] !== undefined &&
                this.compsTableData.rowData[a.ticker].market_data[colKey][
                  sortKey
                ] !== null
                  ? this.compsTableData.rowData[a.ticker].market_data[colKey][
                      sortKey
                    ]
                  : -99999;
              bSliData =
                this.compsTableData.rowData[b.ticker] &&
                this.compsTableData.rowData[b.ticker].market_data &&
                this.compsTableData.rowData[b.ticker].market_data[colKey] &&
                this.compsTableData.rowData[b.ticker].market_data[colKey][
                  sortKey
                ] !== undefined &&
                this.compsTableData.rowData[b.ticker].market_data[colKey][
                  sortKey
                ] !== null
                  ? this.compsTableData.rowData[b.ticker].market_data[colKey][
                      sortKey
                    ]
                  : -99999;
            } else {
              aSliData = this.isTimeSeries
                ? this.compsTableData.rowData[a.ticker] &&
                  this.compsTableData.rowData[a.ticker][
                    this.compsTableData.columnData[colKey].sliCompsId
                  ]
                : this.compsTableData.rowData[a.ticker] &&
                  this.compsTableData.rowData[a.ticker][colKey];
              aSliData =
                aSliData &&
                aSliData[period] &&
                aSliData[period][sortKey] !== undefined
                  ? aSliData[period][sortKey]
                  : -99999;
              bSliData = this.isTimeSeries
                ? this.compsTableData.rowData[b.ticker] &&
                  this.compsTableData.rowData[b.ticker][
                    this.compsTableData.columnData[colKey].sliCompsId
                  ]
                : this.compsTableData.rowData[b.ticker] &&
                  this.compsTableData.rowData[b.ticker][colKey];
              bSliData =
                bSliData &&
                bSliData[period] &&
                bSliData[period][sortKey] !== undefined
                  ? bSliData[period][sortKey]
                  : -99999;
            }

            if (this.sortDirection === "asc") {
              if (aSliData === undefined && bSliData === undefined) {
                return 1;
              }
              if (aSliData === undefined) {
                return -1;
              }
              if (bSliData === undefined) {
                return 1;
              }
              if (aSliData === "NM" && bSliData === "NM") {
                return 1;
              }
              if (aSliData === "NM") {
                return -1;
              }
              if (bSliData === "NM") {
                return 1;
              }
              return aSliData - bSliData;
            }
            if (aSliData === undefined) {
              return 1;
            }
            if (bSliData === undefined) {
              return -1;
            }
            if (aSliData === "NM") {
              return 1;
            }
            if (bSliData === "NM") {
              return -1;
            }
            return bSliData - aSliData;
          });
        }
        this.table.renderRows();
        break;
    }
    setTimeout(() => {
      this.table.updateStickyColumnStyles();
    });
  }

  showFormulaBar() {
    this.store.dispatch(new SetAuditCallFbar({ expanded: true }));
  }

  dismissSnackBar() {
    if (this.snackBarRef) {
      this.snackBarRef.dismiss();
    }
  }

  checkIfMarketKey(key) {
    return Number(key) === 10 || Number(key) === 11 || Number(key) === 34;
  }

  deleteTicker(row: CompsTableRow) {
    this.onDeleteTicker.emit(row);
  }
}
